import axios from "axios";
import { createContext, useMemo } from "react";
import { useQueryClient } from "react-query";

import { getRequestHeaders } from "../utils/getRequestHeaders";

export interface LazyUnitTokenFetchingContext {
    getUnitToken(): Promise<string | undefined>;
}
export const LazyUnitTokenFetchingContext = createContext<LazyUnitTokenFetchingContext>({
    getUnitToken: () => Promise.resolve(undefined),
});

type ICreateTokenResponse =
    | {
          customer_token: string;
          expires_in: number;
      }
    | {
          customer_token: undefined;
          expires_in: undefined;
      };

const KEY = ["token", "fromJwt"];

export const LazyUnitTokenFetchingContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const queryClient = useQueryClient();

    const value = useMemo(
        (): LazyUnitTokenFetchingContext => ({
            getUnitToken: async () => {
                const { customer_token: token, expires_in: expiresInSecs } = await queryClient.fetchQuery(
                    KEY,
                    async (): Promise<ICreateTokenResponse> =>
                        (await axios.post("tokens/from_jwt", undefined, { headers: await getRequestHeaders() })).data,
                    // We'll manually invalidate this based on the response.
                    { staleTime: Infinity },
                );

                if (token == null) {
                    return undefined;
                }

                setTimeout(() => queryClient.invalidateQueries(KEY), expiresInSecs * 1000);
                return token;
            },
        }),
        [queryClient],
    );

    return <LazyUnitTokenFetchingContext.Provider value={value}>{children}</LazyUnitTokenFetchingContext.Provider>;
};
